import React, { useState, useEffect } from "react";
import Dashboard from "components/dashboard";
import { PageHeaderBar, PageSection } from "components/page";
import { QueryResponseHandler } from "components/graphql";
import { DeploymentData, ActionButtons, AddSourceButton, changeDeploymentPrivacyMutation } from "./details-page";
import { DeploymentSourcePaginatedTable } from "../source";
import { Restricted, permissions } from "components/auth";
import { parsePage } from "components/table";
import { useCurrentCustomerContext } from "components/current-customer-context";
import { deleteDeploymentMutation } from "./table";
import { useLoadingContext } from "components/loading-spinner-overlay";
import { useMutation } from "@apollo/react-hooks";
import { isPrivacyActivated } from "pages/deployment";
import { useImperativeTracking } from "components/tracking";
import GroupsTable from "./details-page/subdeployment/groups-table/groups-table";
import { storeDeploymentSourcesActionCreator } from "../../actions/common-actions";
import AddSubdeploymentButton from "./details-page/subdeployment/add-subdeployment-button";
import { getDeploymentService } from "../../services/deploymentServices";

const DeploymentDetailsPage = ({
  match: {
    params: { id },
  },
  location: { search },
  history,
}) => {
  const track = useImperativeTracking();
  const { currentCustomerId } = useCurrentCustomerContext();
  const { setLoading } = useLoadingContext();
  const [changeDeploymentPrivacy] = useMutation(changeDeploymentPrivacyMutation);
  const [deployment, setDeployment] = useState(null);
  const [loading, setLoadingState] = useState(true);
  const [error, setError] = useState(null);

  const getDeployment = () => {
    setLoadingState(true);
    getDeploymentService(
      { deploymentId: id },
      deployment => {
        setDeployment(deployment);
        setLoadingState(false);
      },
      err => {
        setError(err);
        setLoadingState(false);
      },
    );
  };

  const deploymentName = deployment ? deployment.codename : "";

  const variables = deployment
    ? {
        deploymentId: deployment._id,
        privacy: !isPrivacyActivated(deployment.privacy_activated),
      }
    : {};

  const handleChangePrivacy = () => {
    setLoading(true);
    track(`User changed deployment privacy for [${variables.deploymentId}] to [${variables.privacy}]`);
    changeDeploymentPrivacy({ variables })
      .then(() => {
        getDeployment();
      })
      .finally(() => setLoading(false));
  };

  useEffect(
    () => {
      getDeployment();
    },
    [id],
  );

  return (
    <Dashboard>
      <QueryResponseHandler error={error} loading={loading} elements={deployment}>
        <PageHeaderBar
          showBackButton
          title="app.page.deployment.details.title"
          titleParams={{ deploymentName: deployment ? deployment.codename : null }}
          buttons={
            <ActionButtons
              id={id}
              customerId={currentCustomerId}
              name={deploymentName}
              mutation={deleteDeploymentMutation}
              history={history}
              deploymentId={id}
            />
          }
        />
        <PageSection>
          <DeploymentData id={id} deployment={deployment} onChangePrivacy={handleChangePrivacy} />
        </PageSection>

        <>
          <PageHeaderBar
            title="app.page.camera.groups"
            titleParams={{ deploymentName: deployment ? deployment.codename : null }}
            buttons={
              <Restricted permissions={[permissions.deployment_manage]}>
                <AddSubdeploymentButton actionButton deploymentId={id} refetch={getDeployment} />
              </Restricted>
            }
          />
          <PageSection hideBackground>
            <GroupsTable groups={deployment?.subdeployments} refetch={getDeployment} />
          </PageSection>
        </>

        <PageHeaderBar
          subtitleStyles
          title="app.page.source.list"
          buttons={
            <Restricted permissions={[permissions.source_manage, permissions.deployment_manage]}>
              <AddSourceButton actionButton deploymentId={id} onSuccess={() => history.push(0)} />
            </Restricted>
          }
        />

        <PageSection hideBackground>
          <DeploymentSourcePaginatedTable
            page={parsePage({
              queryParams: search,
            })}
            variables={{ deploymentId: id }}
            action={storeDeploymentSourcesActionCreator}
          />
        </PageSection>
      </QueryResponseHandler>
    </Dashboard>
  );
};

export default DeploymentDetailsPage;
